import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { CardBackShell } from 'components/PlayingCard';
import {
  CARDS_PER_HAND,
  DEAL_ROUND_GAP_MS,
  DEAL_FLIGHT_MS,
  DEAL_FINISH_PAD_MS,
  dealOriginCoord,
  displayLayoutForSeat,
  handCardPosition,
  type SeatLayoutProfile,
  type SeatPosition,
} from 'constants/seatLayout';

interface CardDealAnimationProps {
  /** Seat numbers of active players in deal order (clockwise by seatNo) */
  seatNos: number[];
  seatLayoutMap: Map<number, SeatPosition>;
  layoutProfile?: SeatLayoutProfile;
  /** Bumped by parent once per hand — prevents re-running the deal sequence */
  dealKey: number;
  onComplete: () => void;
  onCardDealt?: (seatNo: number, cardRound: number) => void;
}

interface Point {
  x: number;
  y: number;
}

interface MeasuredLayout {
  origin: Point;
  seats: Map<number, Point>;
}

interface FlightStep {
  id: number;
  seatNo: number;
  cardRound: number;
}

/** One round = same card index dealt to every active seat at once */
function buildDealRounds(seatNos: number[]): { seatNo: number; cardRound: number }[][] {
  const rounds: { seatNo: number; cardRound: number }[][] = [];
  for (let round = 0; round < CARDS_PER_HAND; round += 1) {
    rounds.push(seatNos.map((seatNo) => ({ seatNo, cardRound: round })));
  }
  return rounds;
}

function fanOffset(cardRound: number): { rotate: number; x: number } {
  if (cardRound === 0) return { rotate: -10, x: -6 };
  if (cardRound === 2) return { rotate: 10, x: 6 };
  return { rotate: 0, x: 0 };
}

function measureLayout(
  el: HTMLDivElement,
  seatNos: number[],
  seatLayoutMap: Map<number, SeatPosition>,
  layoutProfile: SeatLayoutProfile = 'desktop'
): MeasuredLayout | null {
  const rect = el.getBoundingClientRect();
  if (rect.width < 10 || rect.height < 10) return null;

  const originCoord = dealOriginCoord(layoutProfile);
  const origin: Point = {
    x: (rect.width * originCoord.x) / 100,
    y: (rect.height * originCoord.y) / 100,
  };
  const seats = new Map<number, Point>();
  for (const seatNo of seatNos) {
    const layout = displayLayoutForSeat(seatNo, seatLayoutMap);
    const hand = handCardPosition(layout, layoutProfile);
    seats.set(seatNo, {
      x: (rect.width * hand.x) / 100,
      y: (rect.height * hand.y) / 100,
    });
  }
  return { origin, seats };
}

export default function CardDealAnimation({
  seatNos,
  seatLayoutMap,
  layoutProfile = 'desktop',
  dealKey,
  onComplete,
  onCardDealt,
}: CardDealAnimationProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const layoutRef = useRef<MeasuredLayout | null>(null);
  const [layoutReady, setLayoutReady] = useState(false);
  const [flights, setFlights] = useState<FlightStep[]>([]);
  const completedRef = useRef(false);
  const sequenceStartedRef = useRef(false);
  const onCompleteRef = useRef(onComplete);
  const onCardDealtRef = useRef(onCardDealt);
  onCompleteRef.current = onComplete;
  onCardDealtRef.current = onCardDealt;

  const seatNosKey = useMemo(
    () => [...seatNos].sort((a, b) => a - b).join(','),
    [seatNos]
  );
  const dealRounds = useMemo(
    () => (seatNosKey ? buildDealRounds(seatNosKey.split(',').map(Number)) : []),
    [seatNosKey]
  );

  const finishDeal = () => {
    if (completedRef.current) return;
    completedRef.current = true;
    setFlights([]);
    onCompleteRef.current();
  };

  useLayoutEffect(() => {
    sequenceStartedRef.current = false;
    completedRef.current = false;
    layoutRef.current = null;
    setLayoutReady(false);
    setFlights([]);
  }, [dealKey]);

  useLayoutEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    let cancelled = false;
    let retryTimer: number | undefined;

    const applyMeasure = () => {
      if (cancelled) return false;
      const nos = seatNosKey ? seatNosKey.split(',').map(Number) : [];
      const next = measureLayout(el, nos, seatLayoutMap, layoutProfile);
      if (next) {
        layoutRef.current = next;
        setLayoutReady(true);
        return true;
      }
      return false;
    };

    if (!applyMeasure()) {
      let attempts = 0;
      retryTimer = window.setInterval(() => {
        attempts += 1;
        if (applyMeasure() || attempts >= 40) {
          if (retryTimer !== undefined) window.clearInterval(retryTimer);
          retryTimer = undefined;
        }
      }, 50);
    }

    const raf = window.requestAnimationFrame(applyMeasure);
    return () => {
      cancelled = true;
      window.cancelAnimationFrame(raf);
      if (retryTimer !== undefined) window.clearInterval(retryTimer);
    };
  }, [dealKey, seatNosKey, seatLayoutMap, layoutProfile]);

  useEffect(() => {
    if (!layoutReady || dealRounds.length === 0) return;
    if (sequenceStartedRef.current) return;

    sequenceStartedRef.current = true;
    completedRef.current = false;

    const timers: ReturnType<typeof setTimeout>[] = [];
    const roundDuration = DEAL_FLIGHT_MS + DEAL_ROUND_GAP_MS;
    const finishMs =
      Math.max(0, dealRounds.length - 1) * roundDuration + DEAL_FLIGHT_MS + DEAL_FINISH_PAD_MS;

    let stepId = 0;
    dealRounds.forEach((roundSteps, roundIndex) => {
      const startMs = roundIndex * roundDuration;
      roundSteps.forEach((step) => {
        const id = stepId;
        stepId += 1;
        timers.push(
          setTimeout(() => {
            setFlights((prev) => [
              ...prev,
              { id, seatNo: step.seatNo, cardRound: step.cardRound },
            ]);
          }, startMs)
        );
      });
    });

    timers.push(
      setTimeout(() => {
        finishDeal();
      }, finishMs)
    );

    return () => {
      timers.forEach(clearTimeout);
    };
  }, [dealKey, layoutReady, seatNosKey, dealRounds]);

  useEffect(() => {
    if (dealRounds.length === 0 || layoutReady) return;
    const t = window.setTimeout(() => {
      if (!layoutRef.current) finishDeal();
    }, 2200);
    return () => window.clearTimeout(t);
  }, [dealKey, layoutReady, seatNosKey, dealRounds]);

  const layout = layoutRef.current;

  return (
    <div ref={containerRef} className="pointer-events-none absolute inset-0 z-[42] overflow-visible">
      {layout &&
        flights.map((flight) => {
          const target = layout.seats.get(flight.seatNo);
          if (!target) return null;
          const seatY = handCardPosition(
            displayLayoutForSeat(flight.seatNo, seatLayoutMap),
            layoutProfile
          ).y;
          return (
            <FlyingCard
              key={`${dealKey}-${flight.id}`}
              origin={layout.origin}
              target={target}
              fan={fanOffset(flight.cardRound)}
              seatY={seatY}
              onLanded={() => {
                onCardDealtRef.current?.(flight.seatNo, flight.cardRound);
              }}
              onDone={() => {
                setFlights((prev) => prev.filter((f) => f.id !== flight.id));
              }}
            />
          );
        })}
    </div>
  );
}

function FlyingCard({
  origin,
  target,
  fan,
  seatY,
  onLanded,
  onDone,
}: {
  origin: Point;
  target: Point;
  fan: { rotate: number; x: number };
  seatY: number;
  onLanded?: () => void;
  onDone: () => void;
}) {
  const isBottom = seatY >= 70;
  const endX = target.x + fan.x;
  const endY = target.y;
  const doneRef = useRef(false);
  const landedRef = useRef(false);

  const handleDone = () => {
    if (!landedRef.current) {
      landedRef.current = true;
      onLanded?.();
    }
    if (doneRef.current) return;
    doneRef.current = true;
    onDone();
  };

  return (
    <motion.div
      className="absolute z-[50] will-change-[left,top]"
      style={{ width: 0, height: 0 }}
      initial={{ left: origin.x, top: origin.y }}
      animate={{ left: endX, top: endY }}
      transition={{ duration: DEAL_FLIGHT_MS / 1000, ease: [0.22, 0.61, 0.36, 1] }}
      onAnimationComplete={handleDone}
    >
      <motion.div
        initial={{ scale: 0.45, rotate: -18, opacity: 0.7 }}
        animate={{
          scale: isBottom ? 1 : 0.92,
          rotate: fan.rotate,
          opacity: 1,
        }}
        transition={{ duration: DEAL_FLIGHT_MS / 1000, ease: [0.22, 0.61, 0.36, 1] }}
        style={{ transform: 'translate(-50%, -50%)' }}
      >
        <CardBackShell compact medium={!isBottom} small={isBottom} />
      </motion.div>
    </motion.div>
  );
}
